import { Controller } from "@hotwired/stimulus"
import { syncPageScrollLock } from "../src/page_scroll_lock"

// Bottom sheet built on a native <dialog> shown with showModal(). The top layer
// gives us, for free, everything the previous implementation hand-rolled:
// viewport positioning regardless of transformed or stacking-context ancestors,
// an inert background, a focus trap, focus restoration on close and Escape
// handling. What is left here is the open/close animation, the lazy-load gate,
// nesting and the page scroll lock.

const CLOSE_TRANSITION_FALLBACK_MS = 350
const OPEN_EVENT = "bottom-drawer:open"

// One delegated click listener serves every drawer on the page. Each drawer
// registers its trigger ids here instead of adding its own document-level
// listener, so a tap costs a single ancestor walk regardless of how many
// drawers the page renders (product pages render 40+).
const triggerIdToControllers = new Map()

function handleDelegatedTriggerClick(event) {
  const target = event.target
  let node = target.nodeType === Node.ELEMENT_NODE ? target : target.parentElement

  while (node && node !== document.documentElement) {
    const controllers = node.id && triggerIdToControllers.get(node.id)

    if (controllers && controllers.size > 0) {
      controllers.forEach(controller => controller.open())
      return
    }

    node = node.parentElement
  }
}

document.addEventListener("click", handleDelegatedTriggerClick)

// Runs the callback once the closing slide-down has finished, or after a
// fallback timeout when no transition runs (reduced motion, hidden tab).
function afterCloseTransition(content, callback) {
  let fallbackTimer

  const finish = () => {
    content.removeEventListener("transitionend", onTransitionEnd)
    clearTimeout(fallbackTimer)
    callback()
  }

  const onTransitionEnd = event => {
    if (event.target === content && event.propertyName === "transform") finish()
  }

  content.addEventListener("transitionend", onTransitionEnd)
  fallbackTimer = setTimeout(finish, CLOSE_TRANSITION_FALLBACK_MS)
}

export default class extends Controller {
  static values = {
    maxHeight: { type: Number, default: 100 },
    defaultHeightMobile: { type: Number, default: 50 },
    defaultHeightDesktop: { type: Number, default: 50 }
  }

  connect() {
    this.closeToken = 0
    this.handleClick = this.click.bind(this)
    this.handleCancel = this.cancel.bind(this)
    this.handleClosed = this.cleanUpAfterClose.bind(this)
    this.handleOpenEvent = () => this.open()

    this.element.addEventListener("click", this.handleClick)
    this.element.addEventListener("cancel", this.handleCancel)
    this.element.addEventListener("close", this.handleClosed)
    this.element.addEventListener(OPEN_EVENT, this.handleOpenEvent)

    // Kept for callers that predate the bottom-drawer:open event (Alpine
    // @click handlers in views).
    this.element.showSheet = () => this.open()
    this.element.hideSheet = () => this.close()

    this.triggerIds.forEach(id => {
      if (!triggerIdToControllers.has(id)) triggerIdToControllers.set(id, new Set())
      triggerIdToControllers.get(id).add(this)
    })
    this.refreshBackButtons()

    // A cached Turbo snapshot restores the `open` attribute, but the restored
    // dialog is non-modal (the top layer is not part of the snapshot). Show it
    // modally again so it behaves like a drawer the visitor just opened.
    if (this.element.open) {
      this.element.removeAttribute("open")
      this.open()
    }
  }

  disconnect() {
    cancelAnimationFrame(this.openFrame)
    this.element.removeEventListener("click", this.handleClick)
    this.element.removeEventListener("cancel", this.handleCancel)
    this.element.removeEventListener("close", this.handleClosed)
    this.element.removeEventListener(OPEN_EVENT, this.handleOpenEvent)
    delete this.element.showSheet
    delete this.element.hideSheet
    this.triggerIds.forEach(id => triggerIdToControllers.get(id)?.delete(this))

    // The element leaves the DOM with `open` still set, so derive the lock from
    // whatever is left on the page rather than from this drawer's own state.
    syncPageScrollLock()
  }

  open() {
    const content = this.content
    const body = this.body
    if (!content || !body) return

    // Invalidate any close still waiting on its transition, so a reopen during
    // the slide-down does not get torn down when that close finishes.
    this.closeToken += 1
    cancelAnimationFrame(this.openFrame)

    this.applyDefaultHeight()
    this.refreshBackButtons()
    // The body holds a lazily loaded Turbo Frame, so it stays hidden until the
    // drawer opens and the frame is allowed to fetch.
    body.classList.remove("hidden")

    if (!this.element.open) this.element.showModal()

    // The top layer does not escape a display:none ancestor (a collapsed x-show
    // panel, a hidden responsive copy sharing the trigger id). Such a drawer
    // renders nothing, yet as the topmost modal it would make the whole page,
    // including any drawer that did open, inert.
    if (this.element.checkVisibility?.() === false) {
      this.element.close()
      return
    }

    syncPageScrollLock()
    this.toggleTriggerIcons(true)

    // Commit the offscreen layout first so Android can animate only the transform.
    content.getBoundingClientRect()
    this.openFrame = requestAnimationFrame(() => {
      this.element.classList.add("show")
      this.focusInitialElement()
    })
  }

  close() {
    if (!this.element.open) return

    // A drawer nested in this one is a DOM descendant, so it would be left open
    // (and holding the scroll lock) inside a hidden ancestor.
    this.element.querySelectorAll("dialog.bottom-drawer[open]").forEach(nested => {
      this.application.getControllerForElementAndIdentifier(nested, "bottom-drawer")?.close()
    })

    const token = (this.closeToken += 1)
    cancelAnimationFrame(this.openFrame)
    this.element.classList.remove("show")
    this.toggleTriggerIcons(false)

    const content = this.content
    const finish = () => {
      if (token !== this.closeToken) return

      this.element.close()
      this.cleanUpAfterClose()
    }

    if (content) {
      afterCloseTransition(content, finish)
    } else {
      finish()
    }
  }

  // Dismiss and overlay taps close this drawer and every drawer it is nested in.
  closeAll() {
    this.close()

    let ancestor = this.element.parentElement?.closest(".bottom-drawer")

    while (ancestor) {
      this.application
        .getControllerForElementAndIdentifier(ancestor, "bottom-drawer")
        ?.close()
      ancestor = ancestor.parentElement?.closest(".bottom-drawer")
    }
  }

  click(event) {
    const target = event.target.nodeType === Node.ELEMENT_NODE ? event.target : event.target.parentElement
    if (!target) return

    // The nesting checks keep a click inside a nested drawer from also firing
    // the surrounding drawer's handlers.
    const backButton = target.closest(".back-drawer-button")
    if (backButton && backButton.closest(".bottom-drawer") === this.element) {
      this.close()
      return
    }

    const dismissButton = target.closest(".dismiss-drawer-button")
    if (dismissButton && dismissButton.closest(".bottom-drawer") === this.element) {
      this.closeAll()
      return
    }

    const overlay = target.closest(".overlay")
    if (overlay && overlay.closest(".bottom-drawer") === this.element) this.closeAll()
  }

  // Escape. Take over the browser's instant close so the drawer slides out.
  cancel(event) {
    event.preventDefault()
    this.close()
  }

  // Runs after our own animated close, and from the dialog's close event for
  // anything else that closes it (a form with method="dialog", a stray close()
  // call), so the post-close state is the same either way. Safe to run twice; a
  // drawer reopened in the meantime is left alone.
  cleanUpAfterClose() {
    if (this.element.open) return

    this.element.classList.remove("show")
    this.toggleTriggerIcons(false)

    const body = this.body
    if (body) {
      body.classList.add("hidden")
      body.scrollTop = 0
    }

    syncPageScrollLock()
  }

  focusInitialElement() {
    // showModal already moved focus into the dialog. Prefer an explicit
    // autofocus target (the search and form drawers), otherwise focus the
    // dialog itself so a drawer that opens on its own does not paint a focus
    // ring on an action the visitor never chose.
    const autofocusElement = Array.from(this.element.querySelectorAll("[autofocus]")).find(
      element => element.closest(".bottom-drawer") === this.element && element.getClientRects().length > 0
    )

    ;(autofocusElement || this.element).focus({ preventScroll: true })
  }

  applyDefaultHeight() {
    const defaultHeight = window.innerWidth <= 768 ? this.defaultHeightMobileValue : this.defaultHeightDesktopValue
    const height = Math.min(defaultHeight, this.maxHeightValue)

    this.content.style.height = `${height}vh`
    this.element.classList.toggle("fullscreen", height === 100)
  }

  // The back button is only meaningful for a drawer opened from inside another
  // drawer, so it ships hidden and is revealed when this drawer is nested.
  refreshBackButtons() {
    const isNested = this.element.parentElement?.closest(".bottom-drawer") != null

    this.element.querySelectorAll(".back-drawer-button").forEach(button => {
      if (button.closest(".bottom-drawer") !== this.element) return

      button.classList.toggle("hidden", !isNested)
    })
  }

  toggleTriggerIcons(isOpen) {
    this.triggerIds.forEach(id => {
      const button = document.getElementById(id)
      if (!button) return

      button.querySelector(".down-icon")?.classList.toggle("hidden", isOpen)
      button.querySelector(".up-icon")?.classList.toggle("hidden", !isOpen)
    })
  }

  get triggerIds() {
    return (this.element.dataset.triggerId || "")
      .split(",")
      .map(id => id.trim())
      .filter(Boolean)
  }

  get content() {
    return this.element.querySelector(".drawer-content")
  }

  get body() {
    return this.element.querySelector(".body")
  }
}
